import React, { useMemo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import AppIcon from './AppIcon';
import { pinyin } from 'pinyin-pro';
import './SiteSearch.css';

/**
 * 站内搜索组件
 * @param {Object} props
 * @param {string} props.searchTerm - 搜索关键词
 * @param {Function} props.onItemClick - 点击搜索结果项的回调
 * @param {boolean} props.visible - 是否显示搜索结果
 */
const SiteSearch = ({ searchTerm, onItemClick, visible }) => {
  const websitesByNav = useSelector(state => state.website.websitesByNav);
  const { showQuickSearch } = useSelector(state => state.settings);
  const scrollContainerRef = useRef(null);

  // 搜索匹配逻辑
  const searchResults = useMemo(() => {
    if (!searchTerm || !searchTerm.trim()) {
      return [];
    }

    const term = searchTerm.toLowerCase().trim();
    const allWebsites = [];

    // 收集所有网站数据 - 根据实际的数据结构
    Object.entries(websitesByNav || {}).forEach(([navId, websites]) => {
      if (Array.isArray(websites)) {
        websites.forEach(website => {
          allWebsites.push({
            ...website,
            navName: navId // 使用导航ID作为分类名称
          });
        });
      }
    });

    // 增强的匹配函数
    const getMatchScore = (website) => {
      const name = website.name || '';
      const description = website.description || '';
      const url = website.url || '';
      
      // 获取拼音和首字母
      const namePinyin = pinyin(name, { toneType: 'none', type: 'string' }).toLowerCase().replace(/\s+/g, '');
      const nameInitials = pinyin(name, { pattern: 'initial', toneType: 'none', type: 'string' }).toLowerCase().replace(/\s+/g, '');
      const descPinyin = pinyin(description, { toneType: 'none', type: 'string' }).toLowerCase().replace(/\s+/g, '');
      const descInitials = pinyin(description, { pattern: 'initial', toneType: 'none', type: 'string' }).toLowerCase().replace(/\s+/g, '');

      // 调试日志 - 只在搜索"dulicangku"时打印
      if (term === 'dulicangku' && name.includes('独立')) {
        
        console.log('namePinyin (before remove space):', pinyin(name, { toneType: 'none', type: 'string' }).toLowerCase());
        console.log('namePinyin (after remove space):', namePinyin);
        console.log('nameInitials:', nameInitials);
        console.log('term:', term);
        console.log('namePinyin includes term:', namePinyin.includes(term));
        console.log('nameInitials includes term:', nameInitials.includes(term));
      }

      // 匹配规则和评分
      let score = 0;

      // 1. 名称完全匹配 (最高优先级)
      if (name.toLowerCase() === term) score += 1000;
      if (namePinyin === term) score += 950;
      if (nameInitials === term) score += 900;

      // 2. 名称开头匹配 (高优先级)
      if (name.toLowerCase().startsWith(term)) score += 800;
      if (namePinyin.startsWith(term)) score += 750;
      if (nameInitials.startsWith(term)) score += 700;

      // 3. 名称包含关键词 (中等优先级)
      if (name.toLowerCase().includes(term)) score += 600;
      if (namePinyin.includes(term)) score += 550;
      if (nameInitials.includes(term)) score += 500;

      // 4. 描述匹配 (中等优先级)
      if (description.toLowerCase().includes(term)) score += 400;
      if (descPinyin.includes(term)) score += 350;
      if (descInitials.includes(term)) score += 300;

      // 5. URL匹配 (低优先级)
      if (url.toLowerCase().includes(term)) score += 200;

      // 6. 导航分类匹配
      const navPinyin = pinyin(website.navName || '', { toneType: 'none', type: 'string' }).toLowerCase().replace(/\s+/g, '');
      const navInitials = pinyin(website.navName || '', { pattern: 'initial', toneType: 'none', type: 'string' }).toLowerCase().replace(/\s+/g, '');
      if ((website.navName || '').toLowerCase().includes(term)) score += 150;
      if (navPinyin.includes(term)) score += 120;
      if (navInitials.includes(term)) score += 100;

      return score;
    };

    // 过滤和排序
    const filteredWebsites = allWebsites
      .map(website => ({
        ...website,
        score: getMatchScore(website)
      }))
      .filter(website => website.score > 0)
      .sort((a, b) => b.score - a.score)
      // 去重：使用URL作为唯一标识
      .filter((website, index, self) => 
        index === self.findIndex((w) => w.url === website.url)
      )
      .slice(0, 8); // 限制显示8个结果，适合一行显示

    return filteredWebsites;
  }, [searchTerm, websitesByNav]);
  
  // 添加鼠标滚轮支持
  useEffect(() => {
    if (visible && searchResults.length > 0) {
      const handleWheel = (e) => {
        // **重要**: 检查是否正在拖拽，如果是则不处理滚轮事件
        if (document.body.hasAttribute('data-dragging') || 
            window._isAppDragging || 
            window._dndKitDragging) {
          return;
        }
        
        // 查找滚动容器
        const container = scrollContainerRef.current?.querySelector('.site-search-container');
        const dropdown = scrollContainerRef.current;
        
        // 扩大触发区域：只要鼠标在整个下拉框区域内就触发
        if (container && dropdown && dropdown.contains(e.target)) {
          e.preventDefault();
          e.stopPropagation();
          
          // 处理水平和垂直滚轮
          let scrollAmount = 0;
          
          // 如果有水平滚轮输入，优先使用
          if (e.deltaX !== 0) {
            scrollAmount = e.deltaX * 6; // 大幅增加水平滚轮敏感度
          } else if (e.deltaY !== 0) {
            // 将垂直滚动转换为水平滚动，大幅增加敏感度
            scrollAmount = e.deltaY * 8; // 大幅增加垂直滚轮敏感度
          }
          
          if (scrollAmount !== 0) {
            // 使用 requestAnimationFrame 让滚动更流畅
            requestAnimationFrame(() => {
              container.scrollLeft += scrollAmount;
            });
          }
        }
      };

      const handleKeyDown = (e) => {
        const container = scrollContainerRef.current?.querySelector('.site-search-container');
        const dropdown = scrollContainerRef.current;
        
        // 检查焦点是否在搜索区域内
        if (container && dropdown && (dropdown.contains(document.activeElement) || dropdown.contains(e.target))) {
          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            // 使用平滑滚动
            container.scrollBy({
              left: -100,
              behavior: 'smooth'
            });
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            // 使用平滑滚动
            container.scrollBy({
              left: 100,
              behavior: 'smooth'
            });
          }
        }
      };

      // 添加事件监听器
      document.addEventListener('wheel', handleWheel, { passive: false });
      document.addEventListener('keydown', handleKeyDown);
      
      return () => {
        document.removeEventListener('wheel', handleWheel);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [visible, searchResults.length]);
  
  // 添加触摸和鼠标拖拽滚动支持
  useEffect(() => {
    if (visible && searchResults.length > 0) {
      const container = scrollContainerRef.current?.querySelector('.site-search-container');
      if (!container) return;
      
      let isMouseDown = false;
      let startX = 0;
      let scrollLeft = 0;
      let isTouching = false;
      let lastX = 0;
      let velocity = 0;
      let animationId = null;
      
      // 惯性滚动函数
      const inertiaScroll = () => {
        if (Math.abs(velocity) > 0.5) {
          container.scrollLeft += velocity;
          velocity *= 0.95; // 减速因子
          animationId = requestAnimationFrame(inertiaScroll);
        }
      };
      
      // 鼠标拖拽滚动
      const handleMouseDown = (e) => {
        if (e.button !== 0) return; // 只处理左键
        isMouseDown = true;
        startX = e.pageX - container.offsetLeft;
        lastX = e.pageX;
        scrollLeft = container.scrollLeft;
        velocity = 0;
        container.style.cursor = 'grabbing';
        container.style.scrollBehavior = 'auto'; // 拖拽时禁用平滑滚动
        e.preventDefault();
        
        // 停止任何正在进行的惯性滚动
        if (animationId) {
          cancelAnimationFrame(animationId);
          animationId = null;
        }
      };
      
      const handleMouseMove = (e) => {
        if (!isMouseDown) return;
        e.preventDefault();
        const x = e.pageX - container.offsetLeft;
        const walk = (x - startX) * 3; // 大幅增加拖拽敏感度
        container.scrollLeft = scrollLeft - walk;
        
        // 计算速度用于惯性滚动
        velocity = (lastX - e.pageX) * 0.5;
        lastX = e.pageX;
      };
      
      const handleMouseUp = () => {
        if (isMouseDown) {
          isMouseDown = false;
          container.style.cursor = 'grab';
          container.style.scrollBehavior = 'smooth'; // 恢复平滑滚动
          
          // 开始惯性滚动
          if (Math.abs(velocity) > 1) {
            inertiaScroll();
          }
        }
      };
      
      const handleMouseLeave = () => {
        if (isMouseDown) {
          isMouseDown = false;
          container.style.cursor = 'grab';
          container.style.scrollBehavior = 'smooth'; // 恢复平滑滚动
          
          // 开始惯性滚动
          if (Math.abs(velocity) > 1) {
            inertiaScroll();
          }
        }
      };
      
      // 触摸滑动
      const handleTouchStart = (e) => {
        isTouching = true;
        startX = e.touches[0].pageX - container.offsetLeft;
        lastX = e.touches[0].pageX;
        scrollLeft = container.scrollLeft;
        velocity = 0;
        container.style.scrollBehavior = 'auto'; // 触摸时禁用平滑滚动
        
        // 停止任何正在进行的惯性滚动
        if (animationId) {
          cancelAnimationFrame(animationId);
          animationId = null;
        }
      };
      
      const handleTouchMove = (e) => {
        if (!isTouching) return;
        e.preventDefault();
        const x = e.touches[0].pageX - container.offsetLeft;
        const walk = (x - startX) * 3; // 大幅增加触摸敏感度
        container.scrollLeft = scrollLeft - walk;
        
        // 计算速度用于惯性滚动
        velocity = (lastX - e.touches[0].pageX) * 0.5;
        lastX = e.touches[0].pageX;
      };
      
      const handleTouchEnd = () => {
        if (isTouching) {
          isTouching = false;
          container.style.scrollBehavior = 'smooth'; // 恢复平滑滚动
          
          // 开始惯性滚动
          if (Math.abs(velocity) > 1) {
            inertiaScroll();
          }
        }
      };
      
      // 设置初始样式
      container.style.cursor = 'grab';
      container.style.scrollBehavior = 'smooth';
      
      // 添加事件监听器
      container.addEventListener('mousedown', handleMouseDown);
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseup', handleMouseUp);
      container.addEventListener('mouseleave', handleMouseLeave);
      container.addEventListener('touchstart', handleTouchStart, { passive: false });
      container.addEventListener('touchmove', handleTouchMove, { passive: false });
      container.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        // 清理惯性滚动动画
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
        
        container.removeEventListener('mousedown', handleMouseDown);
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseup', handleMouseUp);
        container.removeEventListener('mouseleave', handleMouseLeave);
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [visible, searchResults.length]);
  
  // 处理点击事件
  const handleItemClick = (website) => {
    // 触发网站点击事件，用于统计
    const clickEvent = new CustomEvent('websiteClick', {
      detail: { website }
    });
    window.dispatchEvent(clickEvent);
    // 在新标签页打开网站
    window.open(website.url, '_blank');
    
    // 如果有回调函数，调用它
    if (onItemClick) {
      onItemClick(website);
    }
  };
  
  // 如果快捷搜索被禁用或没有搜索结果或不可见，不渲染
  if (!showQuickSearch || !visible || searchResults.length === 0) {
    return null;
  }
  
  return (
    <div className="site-search-dropdown" ref={scrollContainerRef}>
      <div className="site-search-container">
        {searchResults.map((website) => (
          <div
            key={`${website.id || website.complexId}-${website.navName}`}
            className="site-search-item"
            onClick={() => handleItemClick(website)}
          >
            <div className="search-item-content">
              <div className="search-item-icon">
                <AppIcon 
                  ico={website.ico}
                  iconUrl={website.iconUrl}
                  textIcon={website.textIcon}
                  bgColor={website.bgColor}
                  preferredIconType={website.preferredIconType}
                  iconColor={website.iconColor || 'white'}
                  size={32}
                  wrapperStyle={{
                    borderRadius: '6px',
                    width: '32px',
                    height: '32px',
                    flexShrink: 0
                  }}
                />
              </div>
              <div className="search-item-info">
                <div className="search-item-name" title={website.name}>
                  {website.name}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SiteSearch;